import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { mountRootParcel, Parcel } from 'single-spa';
import { Button, Layout, Typography } from '@robinpowered/ui-kit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPenToSquare,
  faXmark,
} from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import {
  AuthBridgeRef,
  parseEventParam,
  useEmitSiderOccupancy,
  useFeatureFlags,
  useSiderForegroundDenied,
  useSiderOccupancy,
  useSiderRequestForeground,
} from '@robinpowered/dashboard-apps-common';
import { useTranslation } from 'react-i18next';
import { Sentry } from 'lib/sentry';
import { config } from 'config';
import {
  SHELL_AGENT_SIDER_ID,
  closeAgentChat,
  isAgentChatOpen,
  openAgentChat,
} from './agentChatParam';
import { useRestoreAgentChatOnNavigation } from './useRestoreAgentChatOnNavigation';
import {
  AgentShellEvent,
  buildViewDeskPath,
  buildViewSpacePath,
} from './agentShellEvents';

const { Sider } = Layout;

// Matches the shell composer / office details sider width so slot handoffs
// between right-hand siders land on the same rectangle (see EventOverlayHost).
const SIDER_WIDTH = 340;

type AgentChatSidebarProps = {
  authBridge: AuthBridgeRef;
};

/**
 * The app's single-spa bundle (`<config.agentChatUrl>/agent-chat-app.js`) exports
 * bootstrap/mount/unmount lifecycles and reads auth from the `authBridge` customProp
 *
 * Visibility is driven by the canonical `agent` query param (SHELL_AGENT_PARAM).
 * The Sider is an always-mounted sibling of the MFE mount container, so
 * opening/closing never mutates the pathname and the underlying MFE is unaffected.
 * The param persists across app transitions — navigations that change the
 * pathname without carrying it forward get it restored in place (see
 * useRestoreAgentChatOnNavigation) — so the panel stays open until closed.
 *
 * Participates in the "one sider" policy (common/common/src/types/siderChannel.ts).
 * It yields the slot to the shell event composer whenever the `event` param is present
 * (staying mounted but collapsed so the in-memory conversation survives).
 */
export const AgentChatSidebar = ({
  authBridge,
}: AgentChatSidebarProps): JSX.Element | null => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('globalNavigation');
  const { checkFlag } = useFeatureFlags();

  const enabled =
    !!config.agentChatUrl && checkFlag('hydra(dashboard-agent)', false);

  const isOpen = enabled && isAgentChatOpen(searchParams.toString());
  const isComposerOpen = useMemo(
    () => parseEventParam(searchParams.get('event')).mode !== null,
    [searchParams]
  );
  // Direction 1: The composer opening over us wins.
  // We collapse (but stay mounted, preserving the chat) until it closes.
  const visible = isOpen && !isComposerOpen;

  // Mount the parcel on first open and keep it mounted across collapses — the
  // chat app holds its conversation in memory, so unmounting would discard it.
  const [hasOpened, setHasOpened] = useState(false);
  useEffect(() => {
    if (visible) {
      setHasOpened(true);
    }
  }, [visible]);

  // Keep the panel open across app transitions: nav links and MFE-built URLs
  // assemble their destination from scratch, so a pathname change drops our
  // param and would silently close the panel. Re-applying the param never
  // evicts a composer the destination opened — direction 2 (asking the
  // composer to yield) happens only on the user's Assistant click, in
  // useToggleAgentChat — so a restored panel just backgrounds under it.
  useRestoreAgentChatOnNavigation({ enabled, onRestore: openAgentChat });

  // Direction 2's deny path: the user backed out of the composer's discard
  // dialog after the Assistant click asked it to yield (useToggleAgentChat).
  // Drop our param so the pending open doesn't linger for the next time the
  // composer happens to close.
  useSiderForegroundDenied(
    useCallback(() => {
      if (isOpen && isComposerOpen) {
        closeAgentChat();
      }
    }, [isOpen, isComposerOpen])
  );

  // Direction 2, other side: an MFE sider (office details, etc.) asked for
  // the slot. Unlike the composer we hold no unsaved state, so yield
  // unconditionally by dropping our param — the panel stays mounted, so the
  // conversation survives a later reopen. This also covers the request being
  // aimed at the composer while it covers us (office desk click during
  // compose): if we kept our param, the composer's close would just resurface
  // this panel over the sider the user asked for. Our own requests (the open
  // transition above) carry our id and are skipped.
  useSiderRequestForeground(
    useCallback(
      (id) => {
        if (id !== SHELL_AGENT_SIDER_ID && isOpen) {
          closeAgentChat();
        }
      },
      [isOpen]
    )
  );

  // Whether an MFE right-hand sider occupies the slot, mirrored from
  // EventOverlayHost (see there for the set-of-ids rationale). Presentation
  // only: opening or closing over an occupied slot (office's details sider)
  // swaps instantly — equal-width siders cancel and the map doesn't wobble —
  // while an empty slot (schedule's meeting-rooms grid) gets the standard
  // slide. Our own occupancy reports are skipped: this panel occupying the
  // slot itself says nothing about who it would be handing off with.
  const occupiedIdsRef = useRef<Set<string>>(new Set());
  const [siderOccupied, setSiderOccupied] = useState(false);
  useSiderOccupancy((id, occupied) => {
    if (id === SHELL_AGENT_SIDER_ID) {
      return;
    }
    const ids = occupiedIdsRef.current;
    if (occupied) {
      ids.add(id);
    } else {
      ids.delete(id);
    }
    setSiderOccupied(ids.size > 0);
  });
  const animateSlide = !siderOccupied;

  // Report slot occupancy (content-level, per the channel contract: stays
  // true while the composer covers us) so the composer swaps instantly
  // instead of sliding when it takes over the slot. Emitted on change with an
  // unmount-only `false`, so an isOpen flip doesn't emit a transient `false`
  // between the old effect's cleanup and the new run.
  const emitOccupancy = useEmitSiderOccupancy();
  useEffect(() => {
    emitOccupancy(SHELL_AGENT_SIDER_ID, isOpen);
  }, [isOpen, emitOccupancy]);
  useEffect(
    () => () => emitOccupancy(SHELL_AGENT_SIDER_ID, false),
    [emitOccupancy]
  );

  // Events raised by the agent app (see agentShellEvents.ts). The parcel
  // captures customProps once at mount, so the emitter's identity must stay
  // stable for the life of the parcel; the live handler (which closes over
  // the current `navigate`) is read through a ref instead.
  const navigate = useNavigate();
  const handleAgentEventRef = useRef<(event: AgentShellEvent) => void>(
    () => undefined
  );
  useEffect(() => {
    handleAgentEventRef.current = (event) => {
      // The agent bundle deploys independently, so the compile-time type is
      // not a runtime guarantee; never let a malformed event throw back into
      // the emitting bundle's code.
      if (typeof event !== 'object' || event === null) {
        return;
      }
      // Only navigate while the panel is visible: a hidden panel can't
      // have been clicked, and acting on a late event would wipe the
      // composer's `event` param (losing an unsaved draft with no
      // discard prompt) or yank a user who already closed the assistant.
      const orgSlug = authBridge.authData?.currentOrg.slug;
      switch (event.type) {
        case 'viewDesk': {
          if (!visible || !orgSlug || !event.payload?.deskId) {
            return;
          }
          navigate(buildViewDeskPath(orgSlug, event.payload));
          break;
        }
        case 'viewSpace': {
          if (!visible || !orgSlug || !event.payload?.spaceId) {
            return;
          }
          navigate(buildViewSpacePath(orgSlug, event.payload));
          break;
        }
        default:
          // A newer agent bundle may emit event types this shell doesn't
          // know yet; ignoring them is the contract (agentShellEvents.ts).
          break;
      }
    };
  }, [authBridge, navigate, visible]);
  const onAgentEvent = useCallback(
    (event: AgentShellEvent) => handleAgentEventRef.current(event),
    []
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const parcelRef = useRef<Parcel | null>(null);
  // The most recent parcel teardown. Mounts await it so a rapid flag
  // disable/enable (or retry) can't mount a second parcel into the container
  // while the previous one is still unmounting out of it.
  const teardownRef = useRef<Promise<unknown>>(Promise.resolve());
  const [loadFailed, setLoadFailed] = useState(false);
  // Bumped by the header's new-conversation button to force a fresh
  // mount cycle after the current parcel is torn down.
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    // Depend on reloadToken to trigger remount
    void reloadToken;
    if (
      !hasOpened ||
      loadFailed ||
      parcelRef.current ||
      !containerRef.current
    ) {
      return;
    }
    let cancelled = false;
    void teardownRef.current.then(() => {
      if (cancelled || parcelRef.current || !containerRef.current) {
        return;
      }
      const bundleUrl = `${config.agentChatUrl}/agent-chat-app.js`;
      const parcel = mountRootParcel(
        // webpackIgnore keeps this a native dynamic import so the browser
        // fetches the bundle from the configured origin at runtime.
        () => import(/* webpackIgnore: true */ bundleUrl),
        { domElement: containerRef.current, authBridge, onAgentEvent }
      );
      parcel.mountPromise.catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load the agent chat app', err);
        Sentry.captureException(err);
        // Clear the failed parcel and surface a retry state — otherwise one
        // transient load failure would block the sidebar until a full reload.
        if (parcelRef.current === parcel) {
          parcelRef.current = null;
        }
        setLoadFailed(true);
      });
      parcelRef.current = parcel;
    });
    return () => {
      cancelled = true;
    };
    // authBridge is created once per shell session (AppShell holds it in a
    // ref) and never changes identity, and onAgentEvent is a stable wrapper
    // over a ref; if either ever changes identity, the mounted parcel needs
    // parcel.update({ ... }) rather than a remount.
  }, [hasOpened, loadFailed, reloadToken, authBridge, onAgentEvent]);

  // Tear down the active parcel, per the single-spa parcel contract.
  // Chained on mountPromise because a parcel can't be unmounted mid-transition:
  // if it's already mounted this unmounts immediately, otherwise as soon as
  // mounting finishes. A rejected mountPromise means there's nothing to unmount.
  const unmountParcel = useCallback(() => {
    const parcel = parcelRef.current;
    parcelRef.current = null;
    if (parcel) {
      teardownRef.current = parcel.mountPromise
        .then(() => parcel.unmount())
        .catch(() => undefined);
    }
  }, []);

  // The feature flag can flip off mid-session: the early return below removes
  // the sider DOM but this component stays mounted, so the unmount-on-teardown
  // cleanup never runs. Tear the parcel down here instead, and reset the open
  // and failure state so a later re-enable goes through a fresh cycle.
  useEffect(() => {
    if (!enabled) {
      unmountParcel();
      setHasOpened(false);
      setLoadFailed(false);
    }
  }, [enabled, unmountParcel]);

  // Unmount the parcel with the sider (AppShell teardown).
  useEffect(() => () => unmountParcel(), [unmountParcel]);

  const retryLoad = useCallback(() => setLoadFailed(false), []);

  // Reload the agent: tear down the current parcel (discarding the in-memory
  // conversation) and let the mount effect bring up a fresh one.
  const reloadAgent = useCallback(() => {
    unmountParcel();
    setLoadFailed(false);
    setReloadToken((token) => token + 1);
  }, [unmountParcel]);

  // A collapsed antd Sider keeps its (clipped) children rendered, focusable,
  // and in the accessibility tree; mark the content inert while hidden so
  // tabbing through the page can't land inside the closed panel. Set via ref
  // because the `inert` JSX prop only exists from React 19.
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    contentRef.current?.toggleAttribute('inert', !visible);
  }, [visible]);

  if (!enabled) {
    return null;
  }

  return (
    <RightSider
      $animate={animateSlide}
      collapsed={!visible}
      collapsedWidth={0}
      width={SIDER_WIDTH}
      trigger={null}
      reverseArrow
    >
      <SiderContent ref={contentRef} aria-hidden={!visible}>
        <PanelHeader>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {t('agentChatTitle', 'Robin Agent')}
          </Typography.Title>
          <HeaderActions>
            <Button
              type="text"
              shape="default"
              size="middle"
              icon={<HeaderActionIcon icon={faPenToSquare} />}
              onClick={reloadAgent}
              aria-label={t('agentChatReload', 'New conversation')}
            />
            <Button
              type="text"
              shape="default"
              size="middle"
              icon={<HeaderActionIcon icon={faXmark} />}
              onClick={closeAgentChat}
              aria-label={t('agentChatClose', 'Close assistant')}
            />
          </HeaderActions>
        </PanelHeader>
        <PanelBody>
          <ParcelMount ref={containerRef} $hidden={loadFailed} />
          {loadFailed && (
            <LoadErrorState>
              <Typography.Text>
                {t('agentChatLoadError', 'The assistant failed to load.')}
              </Typography.Text>
              <Button onClick={retryLoad}>
                {t('agentChatRetry', 'Try again')}
              </Button>
            </LoadErrorState>
          )}
        </PanelBody>
      </SiderContent>
    </RightSider>
  );
};

const RightSider = styled(Sider)<{ $animate: boolean }>`
  background: var(--ant-color-bg-container, #ffffff);
  height: 100%;
  overflow: hidden;
  /* Width animation gated by $animate, same rule as EventOverlayHost's sider:
     handing off with an equal-width MFE sider must swap instantly (animating
     would drag the content sideways); an empty slot gets antd's slide. */
  ${({ $animate }) =>
    $animate
      ? ''
      : `&& {
    transition: none;
  }`}
  /* Pin the content to the full sider width so it doesn't reflow while the
     width animates — the sider clips it as it expands (see EventOverlayHost). */
  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
    width: ${SIDER_WIDTH}px;
    transition: none;
  }
`;

const SiderContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ant-margin-xs, 8px);
  min-height: 64px;
  padding: var(--ant-padding, 16px);
  border-bottom: 1px solid var(--ant-color-border-secondary, #f0f0f0);
  flex-shrink: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--ant-margin-xxs, 4px);
  flex-shrink: 0;
`;

/* The design's header glyphs are 16x16; antd buttons default icons to the
   14px base font size, which renders them a step too small and thin. */
const HeaderActionIcon = styled(FontAwesomeIcon)`
  font-size: var(--ant-font-size-lg, 16px);
`;

const PanelBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
`;

const ParcelMount = styled.div<{ $hidden: boolean }>`
  flex: 1;
  min-height: 0;
  display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
  flex-direction: column;

  /* The parcel renders its app root directly into this container; let it
     fill the slot. */
  > * {
    flex: 1;
    min-height: 0;
  }
`;

const LoadErrorState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  text-align: center;
`;
